import { inject } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { targetsOurApi } from '../services/solution-token.service';
import { SKIP_SESSION_RESTORE } from './session-restore.context';

// Marks a request that already went through one failed restore attempt, so a second 401
// passes through instead of retrying forever.
const RESTORE_ATTEMPTED = new HttpContextToken<boolean>(() => false);

/**
 * Revives a session that died mid-flight.
 *
 * A 401 on any non-auth API call usually means the short-lived solution token was
 * rejected — but the server-side session may still be alive. Revalidate it (fresh token +
 * /v1/auth/me), and if it comes back, retry the failed request once; if the session is
 * genuinely dead, drop to the login page.
 *
 * Registered BEFORE solutionTokenInterceptor so the one-time retry re-runs the token
 * attach step and swaps in the freshly-issued bearer token.
 */
export const sessionRestoreInterceptor: HttpInterceptorFn = (req, next) => {
  const isAuthUrl = req.url.includes('/api/v1/auth/');
  if (
    isAuthUrl ||
    !targetsOurApi(req.url) ||
    req.context.get(RESTORE_ATTEMPTED) ||
    req.context.get(SKIP_SESSION_RESTORE)
  ) {
    return next(req);
  }

  // Injected here, not inside the catchError below: the retry runs outside the
  // injection context once the router has ticked, so a late inject() would throw.
  const auth = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      return from(auth.revalidateSession()).pipe(
        switchMap((restored) => {
          if (!restored) {
            return router.navigate(['/login']).then(() => {
              throw error;
            });
          }
          return next(req.clone({ context: req.context.set(RESTORE_ATTEMPTED, true) }));
        })
      );
    })
  );
};